<script setup>
  import BasePage from "@/components/BasePage.vue"
  import { useHead } from "@vueuse/head"

  const socialImage = "https://untrackedapps.com/untracked_apps_logo.png"

  useHead({
    title: "Untracked Apps – Privacy-First Apps",
    meta: [
      {
        name: "description",
        content:
          "Untracked Apps builds beautifully simple, privacy-first apps with transparent data practices.",
      },
      {
        property: "og:title",
        content: "Untracked Apps – Privacy-First Apps",
      },
      {
        property: "og:description",
        content:
          "We design tools for people, not personal data — no ad IDs, no distractions.",
      },
      {
        property: "og:image",
        content: socialImage,
      },
      {
        property: "og:url",
        content: "https://untrackedapps.com/",
      },

      // Twitter Cards
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:title",
        content: "Untracked Apps – Privacy-First Apps",
      },
      {
        name: "twitter:description",
        content:
          "Simple, respectful software built with privacy-first values.",
      },
      {
        name: "twitter:image",
        content: socialImage,
      },
      {
        name: "twitter:site",
        content: "@UntrackedApps",
      },
    ],
    link: [
      {
        rel: "canonical",
        href: "https://untrackedapps.com",
      },
    ],
  })
</script>

<template>
  <BasePage>
    <!-- Hero Section -->
    <section
      class="min-h-[100vh] flex flex-col justify-center items-center px-6 text-center relative overflow-hidden"
    >
      <div class="absolute inset-0 bg-gradient-to-b from-slate-100 via-white to-slate-100"></div>
      <div class="z-10 max-w-3xl">
        <img
          class="main-logo"
          src="../assets/untracked_apps_logo.png"
          alt="Untracked Apps Logo"
        />
        <p class="mt-8 text-2xl md:text-4xl text-gray-800 font-extrabold tracking-tight leading-tight">
          Privacy-first apps for clinicians, learners, and everyday life.
        </p>
        <p class="mt-4 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
          No hidden tracking and no data brokerage. Just focused tools that do one job really well.
        </p>
        <div class="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <router-link
              to="/products"
              class="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition"
          >
            Explore Products
          </router-link>
          <router-link
              :to="{ name: 'privacy' }"
              class="inline-block px-6 py-3 border border-slate-300 text-slate-700 font-semibold rounded-md hover:bg-slate-100 transition"
          >
            Privacy Promise
          </router-link>
        </div>
      </div>
    </section>

    <!-- Top Divider -->
    <div class="h-24 bg-divider"></div>

    <!-- About Section -->
    <section
      class="min-h-[100vh] flex flex-col justify-center items-center px-6 py-20"
    >
      <div class="max-w-3xl text-left">
        <h2 class="text-3xl md:text-4xl text-gray-700 font-semibold mb-6 text-center">
          Who We Are
        </h2>
        <p class="text-lg md:text-xl text-gray-500 leading-relaxed">
          Untracked Apps is an independent software studio focused on building privacy-first tools that respect your
          time and your data. We believe technology should serve you — not exploit you. That's why we maintain a strict
          privacy-first approach across all our products. Whether it's a game, a productivity tool, or something in between,
          we build experiences that feel calm, capable, and respectful — without distraction or privacy concerns.
        </p>
        <p class="text-lg md:text-xl text-gray-500 leading-relaxed mt-4">
          Untracked Apps is a product brand operated by Dominic Juan Lebron and is not a separate legal entity.
        </p>
      </div>
    </section>

    <!-- Bottom Divider -->
    <div class="h-24 bg-divider"></div>

    <!-- Support Section -->
    <section
      class="min-h-[100vh] flex flex-col justify-center items-center px-6 py-20"
    >
      <div class="max-w-3xl">
        <h2 class="text-3xl md:text-4xl text-gray-700 font-semibold mb-6 text-center">
          How You Can Help
        </h2>
        <ul
          class="text-left text-lg md:text-xl text-gray-500 space-y-5 list-disc list-outside pl-6 leading-relaxed"
        >
          <li>
            <span class="text-gray-700 font-medium">Tell a friend.</span> Our
            best growth comes from real word-of-mouth.
          </li>
          <li>
            <span class="text-gray-700 font-medium">Leave a review.</span> It
            helps new users find us organically.
          </li>
          <li>
            <span class="text-gray-700 font-medium">Support our work.</span>
            Purchase premium versions or donate when you can.
          </li>
          <li>
            <span class="text-gray-700 font-medium">Send feedback.</span> We'd
            love to hear what you're building or wishing for.
          </li>
        </ul>
      </div>
    </section>

  </BasePage>
</template>

<style scoped>
  .bg-divider {
    background-color: #000;
    background-image: url("../assets/concrete-wall.jpg");
    background-repeat: repeat;
    opacity: 0.4;
  }

  .main-logo {
    width: 260px;
    max-width: 78vw;
    height: auto;
    margin: 0 auto;
  }
</style>
